/**
 * UI Schemas
 *
 * Zod validation schemas for UI forms and user input.
 * These schemas validate user-facing form data before processing.
 */

import { z } from 'zod';

// ----- Room Occupancy Schemas -----

/**
 * Person schema for room occupancy
 * Discriminated union based on person type (adult or child)
 */
export const PersonSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('adult'),
  }),
  z.object({
    type: z.literal('child'),
    age: z
      .number()
      .min(0, 'Child age must be at least 0')
      .max(17, 'Child age cannot exceed 17')
      .optional(),
  }),
]);

/**
 * Room schema with persons array
 */
export const RoomSchema = z.object({
  persons: z.array(PersonSchema).min(1, 'At least one person per room is required'),
});

/**
 * Room occupancy schema for form validation
 * Validates the complete room selection including:
 * - Number of rooms (1-4)
 * - Persons per room (at least 1)
 * - Child ages (0-17)
 *
 * @example
 * ```ts
 * const occupancy = {
 *   rooms: [
 *     { persons: [{ type: 'adult' }, { type: 'child', age: 8 }] },
 *     { persons: [{ type: 'adult' }, { type: 'adult' }] }
 *   ]
 * };
 *
 * RoomOccupancySchema.parse(occupancy); // ✅ Valid
 * ```
 */
export const RoomOccupancySchema = z.object({
  rooms: z
    .array(RoomSchema)
    .min(1, 'At least one room is required')
    .max(4, 'Maximum 4 rooms allowed'),
});

// ----- Type Exports -----

export type PersonSchemaType = z.infer<typeof PersonSchema>;
export type RoomSchemaType = z.infer<typeof RoomSchema>;
export type RoomOccupancySchemaType = z.infer<typeof RoomOccupancySchema>;
